import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ExportChecklistButton } from './ChecklistPDF';
import { useChecklistInstance } from './useChecklistInstance.js';
import CommonLayout from '../../components/CommonLayout';
import PageTitle from '../../components/PageTitle';
import { Skeleton } from '../../components/Loaders/Skeleton/Skeleton';
import InputWithIcon from '../../components/InputWithIcon';
import { ChevronDown, ChevronUp, Check } from 'lucide-react';
import { cancelButton, pageActionsContainer } from '../../components/Buttons';
import './ChecklistInstance.scss';

function ChecklistInstance() {
    const { instanceId } = useParams();
    const navigate = useNavigate();
    const {
        instance,
        loading,
        error,
        handleCheckChange,
        handleOptionalFieldChange,
        saveOptionalField,
        saveInstance,
    } = useChecklistInstance(instanceId);
    const [expandedItems, setExpandedItems] = useState({});

    useEffect(() => {
        const handleBeforeUnload = () => {
            saveInstance();
        };

        window.addEventListener('beforeunload', handleBeforeUnload);

        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
            saveInstance();
        };
    }, [saveInstance, navigate]);

    const toggleExpand = (itemId) => {
        setExpandedItems(prev => ({ ...prev, [itemId]: !prev[itemId] }));
    };

    // Helper to get value from optionalFieldValues array
    const getOptionalFieldValue = (item, fieldLabel) => {
        if (!item.optionalFieldValues || !Array.isArray(item.optionalFieldValues)) {
            return '';
        }
        const fieldValue = item.optionalFieldValues.find(fv => fv.label === fieldLabel);
        return fieldValue?.value || '';
    };

    // Helper to update optionalFieldValues
    const updateOptionalFieldValue = (item, fieldLabel, newValue) => {
        const currentValues = [...(item.optionalFieldValues || [])];
        const existingIndex = currentValues.findIndex(fv => fv.label === fieldLabel);

        if (existingIndex >= 0) {
            currentValues[existingIndex] = { ...currentValues[existingIndex], value: newValue };
        } else {
            currentValues.push({ label: fieldLabel, value: newValue });
        }

        return currentValues;
    };

    if (loading) {
        return (
            <CommonLayout >
                <div className="checklist-instance-container loading-container">
                    <Skeleton>
                        <Skeleton.Text />
                        <Skeleton.Block height='200px' />
                        <Skeleton.Block height='200px' />
                    </Skeleton>
                </div>
            </CommonLayout>
        );
    }

    if (error) {
        return (
            <CommonLayout>
                <div className="checklist-instance-container">
                    <div className="form-error">{error}</div>
                    <button style={cancelButton} onClick={() => navigate('/checklists')}>
                        חזור לצ׳קליסטים
                    </button>
                </div>
            </CommonLayout>
        );
    }

    if (!instance) {
        return (
            <CommonLayout maxWidth="900px">
                <div className="checklist-instance-container">
                    <div className="form-error">צ׳קליסט לא נמצא</div>
                    <button style={cancelButton} onClick={() => navigate('/checklists')}>
                        חזור לצ׳קליסטים
                    </button>
                </div>
            </CommonLayout>
        );
    }

    return (
        <CommonLayout maxWidth="900px">
            <div className="checklist-instance-container">
                <PageTitle title={instance.name} />

                <div className="checklist-groups">
                    {instance.groups.map((group, groupIdx) => (
                        <div key={group._id || `group-${groupIdx}`} className="checklist-group">
                            <h2 className="group-title">{group.title}</h2>
                            <div className="checklist-items">
                                {group.items.map((item, itemIdx) => {
                                    // item._id is always set by useChecklistInstance hook
                                    const itemKey = item._id;
                                    const isExpanded = expandedItems[itemKey];
                                    const hasOptionalFields = item.optionalFields && item.optionalFields.length > 0;

                                    return (
                                        <div key={itemKey} className={`checklist-item ${item.isChecked ? 'checked' : ''}`}>
                                            <div className="item-main">
                                                <div className="checkbox-container">
                                                    <input
                                                        type="checkbox"
                                                        id={`checkbox-${itemKey}`}
                                                        className="checklist-checkbox"
                                                        checked={item.isChecked || false}
                                                        onChange={() => handleCheckChange(group._id, itemKey)}
                                                    />
                                                    <label htmlFor={`checkbox-${itemKey}`} className="checkbox-label">
                                                        {item.isChecked && <Check size={14} className="check-icon" />}
                                                    </label>
                                                </div>
                                                <span className="item-text">{item.text}</span>

                                                <div className="item-actions">
                                                    {item.lastChanged && (
                                                        <span className="timestamp">
                                                            {new Date(item.lastChanged).toLocaleString('he-IL', {
                                                                day: '2-digit',
                                                                month: '2-digit',
                                                                year: 'numeric',
                                                                hour: '2-digit',
                                                                minute: '2-digit'
                                                            })}
                                                        </span>
                                                    )}
                                                    {hasOptionalFields && (
                                                        <button
                                                            className="expand-btn"
                                                            onClick={() => toggleExpand(itemKey)}
                                                            title={isExpanded ? 'כווץ' : 'הרחב'}
                                                        >
                                                            {isExpanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                                                        </button>
                                                    )}
                                                </div>
                                            </div>

                                            {isExpanded && hasOptionalFields && (
                                                <div className="optional-fields">
                                                    {/* Render details first */}
                                                    {item.optionalFields.filter(f => f.type === 'details').map((field, fieldIndex) => (
                                                        <div key={`details-${fieldIndex}`} className="optional-field">
                                                            <p className="field-details-text">
                                                                {field.value || 'אין פרטים'}
                                                            </p>
                                                        </div>
                                                    ))}

                                                    {/* Render links second */}
                                                    {item.optionalFields.filter(f => f.type === 'link').map((field, fieldIndex) => (
                                                        <div key={`link-${fieldIndex}`} className="optional-field">
                                                            {field.url && (
                                                                <a
                                                                    href={field.url}
                                                                    target="_blank"
                                                                    rel="noopener noreferrer"
                                                                    className="field-link-text"
                                                                >
                                                                    {field.label}
                                                                </a>
                                                            )}
                                                        </div>
                                                    ))}

                                                    {/* Render inputs last */}
                                                    {item.optionalFields.filter(f => f.type === 'input').map((field, fieldIndex) => {
                                                        const fieldValue = getOptionalFieldValue(item, field.label);
                                                        return (
                                                            <div key={`input-${fieldIndex}`} className="optional-field">
                                                                <label className="field-label">{field.label}</label>
                                                                <InputWithIcon
                                                                    placeholder="הוסף מידע..."
                                                                    multiline
                                                                    rows={3}
                                                                    value={fieldValue}
                                                                    onChange={(e) => {
                                                                        const updatedValues = updateOptionalFieldValue(item, field.label, e.target.value);
                                                                        handleOptionalFieldChange(itemKey, updatedValues);
                                                                    }}
                                                                    onBlur={() => saveOptionalField(itemKey)}
                                                                />
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    ))}
                </div>

                <div style={pageActionsContainer}>
                    <ExportChecklistButton
                        instance={instance}
                        onExportComplete={() => {
                            saveInstance();
                            navigate('/checklists');
                        }}
                    />
                </div>
            </div>
        </CommonLayout>
    );
}

export default ChecklistInstance;